import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { UsersIcon, PlusIcon, SearchIcon, FilterIcon, TableIcon, LayoutIcon, XIcon, ChevronDownIcon, BookOpenIcon } from 'lucide-react';
import { RentalLeadsKanban, BuyerLead, generateMockLeads } from './RentalLeadsKanban';
import { RentalLeadsList } from './RentalLeadsList';
import { NewLeadModal } from '../../components/rental/NewLeadModal';
import { toast } from 'sonner';
export function RentalLeads() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [allLeads, setAllLeads] = useState<BuyerLead[]>(generateMockLeads());
  const [isNewLeadModalOpen, setIsNewLeadModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    leadId: '',
    phone: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    budgetMin: '',
    budgetMax: ''
  });
  // Shared lead update handler
  const handleLeadUpdate = (leadId: string, updates: Partial<BuyerLead>) => {
    setAllLeads(prevLeads => prevLeads.map(lead => lead.leadId === leadId ? {
      ...lead,
      ...updates
    } : lead));
  };
  // Shared action history handler
  const addActionToHistory = (leadId: string, action: string, agent: string = 'Agent Curent') => {
    const now = new Date();
    const timestamp = `${now.toISOString().split('T')[0]} ${now.toTimeString().slice(0, 5)}`;
    setAllLeads(prevLeads => prevLeads.map(lead => {
      if (lead.leadId === leadId) {
        return {
          ...lead,
          actionHistory: [...lead.actionHistory, {
            action,
            timestamp,
            agent
          }]
        };
      }
      return lead;
    }));
  };
  const handleNewLeadSubmit = (newLead: BuyerLead) => {
    setAllLeads(prev => [newLead, ...prev]);
    toast.success('Lead nou creat cu succes!');
  };
  const handleResetFilters = () => {
    setFilters({
      leadId: '',
      phone: '',
      dateFrom: '',
      dateTo: '',
      status: '',
      budgetMin: '',
      budgetMax: ''
    });
  };
  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(value => value !== '');
  // Calculate stats
  const stats = [{
    label: 'Total Leaduri',
    value: allLeads.length,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    label: 'Lead Nou',
    value: allLeads.filter(l => l.callStatus === 'nou').length,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    label: 'De Apelat',
    value: allLeads.filter(l => l.callStatus === 'de-apelat').length,
    color: 'text-purple-600',
    bg: 'bg-purple-100'
  }, {
    label: 'Calificate',
    value: allLeads.filter(l => l.qualified).length,
    color: 'text-emerald-600',
    bg: 'bg-emerald-100'
  }];
  return <div className="min-h-screen pb-20" data-id="element-10867">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#132C53] to-[#1e4278] pb-16 pt-10" data-id="element-10868">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-10869" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-10870"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10871">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-10872">
            <div className="text-center md:text-left" data-id="element-10873">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 200
            }} className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 mb-6 shadow-2xl md:hidden" data-id="element-10874">
                <UsersIcon className="w-8 h-8 text-white" data-id="element-10875" />
              </motion.div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-10876">
                Leaduri{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-10877">
                  Chiriași
                </span>
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl" data-id="element-10878">
                Urmărește și convertește potențialii chiriași
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} className="flex items-center space-x-3" data-id="element-10879">
              <button onClick={() => navigate('/inchiriere/leaduri/tutorial')} className="flex items-center space-x-2 px-5 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all" data-id="element-10880">
                <BookOpenIcon className="w-5 h-5" data-id="element-10881" />
                <span data-id="element-10882">Tutorial</span>
              </button>
              <button onClick={() => setIsNewLeadModalOpen(true)} className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all" data-id="element-10883">
                <PlusIcon className="w-5 h-5" data-id="element-10884" />
                <span data-id="element-10885">Lead Nou</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <NewLeadModal isOpen={isNewLeadModalOpen} onClose={() => setIsNewLeadModalOpen(false)} onSubmit={handleNewLeadSubmit} data-id="element-10886" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10" data-id="element-10887">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-10888">
          {stats.map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-slate-100" data-id="element-10889">
              <p className="text-sm text-slate-500 font-medium mb-1" data-id="element-10890">
                {stat.label}
              </p>
              <div className="flex items-center space-x-2" data-id="element-10891">
                <span className={`text-2xl sm:text-3xl font-bold ${stat.color}`} data-id="element-10892">
                  {stat.value}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden min-h-[600px] flex flex-col" data-id="element-10893">
          {/* Toolbar */}
          <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-50/50" data-id="element-10894">
            <div className="flex items-center space-x-4" data-id="element-10895">
              <div className="flex items-center space-x-2 bg-white p-1 rounded-xl border border-slate-200 shadow-sm" data-id="element-10896">
                <button onClick={() => setViewMode('kanban')} className={`p-2 rounded-lg transition-all flex items-center gap-2 ${viewMode === 'kanban' ? 'bg-blue-50 text-blue-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`} data-id="element-10897">
                  <LayoutIcon className="w-5 h-5" data-id="element-10898" />
                  <span className="text-sm font-medium hidden sm:inline" data-id="element-10899">
                    Kanban
                  </span>
                </button>
                <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all flex items-center gap-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`} data-id="element-10900">
                  <TableIcon className="w-5 h-5" data-id="element-10901" />
                  <span className="text-sm font-medium hidden sm:inline" data-id="element-10902">
                    List
                  </span>
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-3 w-full sm:w-auto" data-id="element-10903">
              <div className="relative flex-1 sm:flex-none" data-id="element-10904">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" data-id="element-10905" />
                <input type="text" placeholder="Caută leaduri..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full sm:w-64 pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm" data-id="element-10906" />
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className={`p-2.5 rounded-xl border-2 transition-all shadow-sm relative ${showFilters ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-blue-600'}`} data-id="element-10907">
                <FilterIcon className="w-5 h-5" data-id="element-10908" />
                {hasActiveFilters && <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-600 rounded-full border-2 border-white" data-id="element-10909"></span>}
              </button>
            </div>
          </div>

          {/* Filter Panel - Expandable */}
          <AnimatePresence data-id="element-10910">
            {showFilters && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="overflow-hidden border-b border-slate-200 bg-slate-50" data-id="element-10911">
                <div className="p-6" data-id="element-10912">
                  <div className="flex items-center justify-between mb-4" data-id="element-10913">
                    <h3 className="text-sm font-bold text-slate-700" data-id="element-10914">
                      Filtrează Leaduri
                    </h3>
                    {hasActiveFilters && <button onClick={handleResetFilters} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1" data-id="element-10915">
                        <XIcon className="w-3 h-3" data-id="element-10916" />
                        Resetează
                      </button>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-10917">
                    <div data-id="element-10918">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-10919">
                        ID Lead
                      </label>
                      <input type="text" placeholder="ex: LI00001" value={filters.leadId} onChange={e => setFilters({
                    ...filters,
                    leadId: e.target.value
                  })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white" data-id="element-10920" />
                    </div>

                    <div data-id="element-10921">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-10922">
                        Telefon
                      </label>
                      <input type="text" placeholder="ex: 0721111111" value={filters.phone} onChange={e => setFilters({
                    ...filters,
                    phone: e.target.value
                  })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white" data-id="element-10923" />
                    </div>

                    <div data-id="element-10924">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-10925">
                        Data de la
                      </label>
                      <input type="date" value={filters.dateFrom} onChange={e => setFilters({
                    ...filters,
                    dateFrom: e.target.value
                  })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white" data-id="element-10926" />
                    </div>

                    <div data-id="element-10927">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-10928">
                        Data până la
                      </label>
                      <input type="date" value={filters.dateTo} onChange={e => setFilters({
                    ...filters,
                    dateTo: e.target.value
                  })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white" data-id="element-10929" />
                    </div>

                    <div data-id="element-10930">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-10931">
                        Status
                      </label>
                      <select value={filters.status} onChange={e => setFilters({
                    ...filters,
                    status: e.target.value
                  })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white" data-id="element-10932">
                        <option value="" data-id="element-10933">Toate</option>
                        <option value="nou" data-id="element-10934">Nou</option>
                        <option value="de-apelat" data-id="element-10935">De apelat</option>
                        <option value="nu-raspunde" data-id="element-10936">Nu răspunde</option>
                        <option value="calificare" data-id="element-10937">Calificare</option>
                        <option value="vrea-vizionare" data-id="element-10938">Vrea vizionare</option>
                        <option value="nu-vrea-vizionare" data-id="element-10939">
                          Nu vrea vizionare
                        </option>
                      </select>
                    </div>

                    <div data-id="element-10940">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-10941">
                        Buget minim (EUR)
                      </label>
                      <input type="number" placeholder="ex: 400" value={filters.budgetMin} onChange={e => setFilters({
                    ...filters,
                    budgetMin: e.target.value
                  })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white" data-id="element-10942" />
                    </div>

                    <div data-id="element-10943">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-10944">
                        Buget maxim (EUR)
                      </label>
                      <input type="number" placeholder="ex: 600" value={filters.budgetMax} onChange={e => setFilters({
                    ...filters,
                    budgetMax: e.target.value
                  })} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white" data-id="element-10945" />
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Kanban/List View */}
          <div className="flex-1 p-6 bg-slate-50/30" data-id="element-10946">
            {viewMode === 'kanban' ? <RentalLeadsKanban allLeads={allLeads} onLeadUpdate={handleLeadUpdate} onAddActionToHistory={addActionToHistory} searchQuery={searchQuery} filters={filters} data-id="element-10947" /> : <RentalLeadsList allLeads={allLeads} onLeadUpdate={handleLeadUpdate} onAddActionToHistory={addActionToHistory} searchQuery={searchQuery} filters={filters} data-id="element-10948" />}
          </div>
        </div>
      </div>
    </div>;
}